import { useCallback, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { LineItem as CartLineItem } from 'shared/types/cart/LineItem';
import OutOfStock from 'components/gruenland/atoms/badge/OutOfStock';
import Image from 'components/gruenland/atoms/image';
import Link from 'components/gruenland/atoms/link';
import QuantitySelector from 'components/gruenland/atoms/quantity-selector';
import Typography from 'components/gruenland/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { productTileImage } from 'lib/wardaImages';
import CartItemPrice from './cart-item-price';

interface ClassNames {
  moveToWishlist?: string;
}

interface Props {
  item: CartLineItem;
  classNames?: ClassNames;
  onRemoveItem(): Promise<void>;
  onUpdateItem(quantity: number): Promise<void>;
}

const CartItem: React.FC<Props> = ({ item, onRemoveItem, onUpdateItem }) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const [processing, setProcessing] = useState(false);

  const deleteButtonClassName = classNames('block', {
    'cursor-not-allowed': processing,
    'cursor-pointer': !processing,
  });

  const handleUpdateItem = useCallback(
    async (quantity: number) => {
      if (processing) return;

      setProcessing(true);

      await (quantity < 1 ? onRemoveItem() : onUpdateItem(quantity));

      setProcessing(false);
    },
    [onUpdateItem, onRemoveItem, processing],
  );

  const handleRemoveItem = useCallback(async () => {
    if (processing) return;

    setProcessing(true);
    await onRemoveItem();
    setProcessing(false);
  }, [onRemoveItem, processing]);

  return (
    <div className="flex max-w-full items-stretch justify-start gap-3 py-[18px]">
      <div className="w-[125px] shrink-0 bg-white p-3">
        <div className="relative size-full rounded-sm">
          <Image src={productTileImage(item.variant?.images)} suffix="small" style={{ objectFit: 'contain' }} fill />
        </div>
      </div>
      <div className="max-w-full grow overflow-hidden">
        <div className="flex items-center justify-between">
          <p className="max-w-[85%] overflow-hidden uppercase">
            <Link link={item._url}>{item.name}</Link>
          </p>
          <i data-testid="remove-button" onClick={handleRemoveItem} className={deleteButtonClassName}>
            <TrashIcon stroke="#494949" className="w-5" />
          </i>
        </div>
        <p>
          <span className="text-xs text-neutrals-gray">
            {formatMessage({ id: 'pdp.size', defaultMessage: 'Taglia' })}: {item.variant?.attributes?.size}
          </span>
        </p>
        <p>
          <span className="text-xs text-neutrals-gray">
            {formatMessage({ id: 'pdp.color', defaultMessage: 'Colore' })}:{' '}
            <Typography as="span">{item.variant?.attributes?.colour}</Typography>
          </span>
        </p>
        <p>
          <span className="text-xs text-neutrals-gray">
            {formatMessage({ id: 'pdp.productKey', defaultMessage: 'Codice' })}:{' '}
            <Typography as="span">{item.productKey}</Typography>
          </span>
        </p>

        {!item.variant?.isOnStock && (
          <div className="mt-2">
            <OutOfStock />
          </div>
        )}
        <div className="mt-2">
          <CartItemPrice item={item} />
        </div>
        <div className="mt-4">
          <QuantitySelector
            value={item.count}
            defaultValue={1}
            minValue={0}
            maxValue={item.variant?.availableQuantity}
            disabled={processing}
            onChange={handleUpdateItem}
          />
        </div>
      </div>
    </div>
  );
};

export default CartItem;
